import type { TPlacement } from '@shared/types/placement';
import { string, array, object, mixed } from 'yup';
import { getPlacementDataFromStorage } from '@features/placements/pages/create/helpers';

export const placementFormNames = {
  program: 'firstStep.program',
  host: 'firstStep.host',
  sponsor: 'firstStep.sponsor',
  interviewer: 'firstStep.interviewer',
  aboutWeather: 'firstStep.aboutWeather',
  aboutCommunity: 'firstStep.aboutCommunity',
  culturalOpportunities: 'firstStep.culturalOpportunities',
  webLinks: 'firstStep.webLinks',
  webLinksUrl: 'firstStep.webLinks.url',
  webLinksText: 'firstStep.webLinks.text',
  placementImages: 'firstStep.placementImages',
  placementFiles: 'firstStep.placementFiles',
} as const;

const existingPlacementData = (placementData?: TPlacement) => {
  if (!placementData) return {};

  return {
    program: placementData.program,
    host: placementData.host,
    sponsor: placementData.sponsor,
    interviewer: placementData.interviewer,
    aboutWeather: placementData.weather_note,
    aboutCommunity: placementData.community_note,
    culturalOpportunities: placementData.cultural_opportunities_note,
    webLinks: placementData.links,
    placementImages: placementData.placement_images,
    placementFiles: placementData.placement_files,
  };
};

export const createFirstStepInitialValues = (placementData?: TPlacement) => {
  const cachedPlacementData = getPlacementDataFromStorage() as {
    firstStep: ReturnType<typeof existingPlacementData>;
  };

  return {
    program: '' as unknown as TPlacement['program'],
    host: '' as unknown as TPlacement['host'],
    sponsor: '' as unknown as TPlacement['sponsor'],
    interviewer: '' as unknown as TPlacement['interviewer'],
    aboutWeather: '',
    aboutCommunity: '',
    culturalOpportunities: '',
    webLinks: [{ url: '', text: '' }],
    placementImages: [] as TPlacement['placement_images'],
    placementFiles: [] as TPlacement['placement_files'],

    ...cachedPlacementData?.firstStep,
    ...existingPlacementData(placementData),
  };
};

export const placementFormSchema = object().shape({
  firstStep: object().shape({
    program: mixed().nullable().required('Program selection is required'),
    host: mixed()
      .nullable()
      .required('Please choose an existing host or create new one'),
    sponsor: mixed().nullable(),
    interviewer: mixed().nullable(),
    aboutWeather: string()
      .max(2000, 'Text cannot exceed 2000 characters')
      .nullable(),
    aboutCommunity: string()
      .max(2000, 'Text cannot exceed 2000 characters')
      .nullable(),
    culturalOpportunities: string()
      .max(2000, 'Text cannot exceed 2000 characters')
      .nullable(),
    webLinks: array().of(
      object().shape({
        url: string().url('Must be a valid URL'),
        text: string().when('url', {
          is: (url: string) => !!url,
          then: () =>
            string().required('Link text is required when URL is provided'),
        }),
      })
    ),
    placementImages: array().of(mixed()),
    placementFiles: array().of(mixed()),
  }),
});

export type TPlacementFormValues = ReturnType<
  typeof createFirstStepInitialValues
>;
